import React, { useEffect, useRef, useState } from "react";

import Controls from "../VideoPlayer/Controls";
import SeekBar from "../VideoPlayer/Seekbar";
import { Visibility } from "../VideoPlayer/Visibility";

import { MdOutlineNavigateNext, MdVideocam } from "react-icons/md";
import { GrFormPrevious } from "react-icons/gr";
import { FaPlay } from "react-icons/fa";

import { getMediaUrl } from "../../lib/utils";

const VideoSlider = ({ post, largeScreen = true }) => {
  const postArray = post?.split(",").map((item) => item.trim()).filter(Boolean) || [];

  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const previewVideoRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const fixingDurationRef = useRef(false);
  const fixingPreviewDurationRef = useRef(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [speed, setSpeed] = useState(1);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showVolume, setShowVolume] = useState(false);
  const [showSpeedOptions, setShowSpeedOptions] = useState(false);
  const [hoverProgress, setHoverProgress] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [showViewer, setShowViewer] = useState(false);

  /* ================= VIDEO SOURCE ================= */

  const getVideoSource = (source) => {
    if (!source) return "";

    const trimmedSource = source.trim();

    if (
      trimmedSource.startsWith("blob:") ||
      trimmedSource.startsWith("data:") ||
      trimmedSource.startsWith("http://") ||
      trimmedSource.startsWith("https://")
    ) return trimmedSource;

    return getMediaUrl(trimmedSource);
  };

  /* ================= FEED FIRST FRAME ================= */

  const getVideoThumbnailSource = (source) => {
    return getVideoSource(source);
  };

  const handleFeedPreviewLoadedMetadata = (e) => {
    const video = e.currentTarget;

    try {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        video.currentTime = Math.min(0.1, video.duration / 2);
      }
    } catch (error) {
      console.error("Feed preview frame error:", error);
    }
  };

  const currentVideo = postArray[currentIndex];
  const currentVideoSource = getVideoSource(currentVideo);

  /* ================= FORMAT TIME ================= */

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return "00:00";

    const minutes = Math.floor(seconds / 60).toString().padStart(2, "0");
    const secs = Math.floor(seconds % 60).toString().padStart(2, "0");

    return `${minutes}:${secs}`;
  };

  /* ================= VISIBILITY ================= */

  const isVisible = Visibility(containerRef, 0.5);

  /* ================= AUTO PAUSE ================= */

  useEffect(() => {
    if (!showViewer) return;

    if (!isVisible && !isFullScreen && isPlaying) {
      videoRef.current?.pause();
      setIsPlaying(false);
    }
  }, [isVisible, isFullScreen, isPlaying, showViewer]);

  /* ================= NORMALIZE MAIN DURATION ================= */

  const normalizeMainDuration = () => {
    const video = videoRef.current;

    if (!video) return;

    const value = video.duration;

    if (Number.isFinite(value) && value > 0) {
      fixingDurationRef.current = false;
      setDuration(value);
      return;
    }

    if (value === Infinity && !fixingDurationRef.current) {
      fixingDurationRef.current = true;

      try {
        video.currentTime = 10000000;
      } catch (error) {
        console.error("Duration normalization error:", error);
        fixingDurationRef.current = false;
      }
    }
  };

  /* ================= MAIN DURATION CHANGE ================= */

  const handleDurationChange = () => {
    const video = videoRef.current;

    if (!video) return;

    const value = video.duration;

    if (!Number.isFinite(value) || value <= 0) return;

    setDuration(value);

    if (fixingDurationRef.current) {
      fixingDurationRef.current = false;

      try {
        video.currentTime = 0;
      } catch (error) {
        console.error("Video reset error:", error);
      }

      setCurrentTime(0);
    }
  };

  /* ================= PREVIEW DURATION ================= */

  const normalizePreviewDuration = () => {
    const video = previewVideoRef.current;

    if (!video) return;

    const value = video.duration;

    if (Number.isFinite(value) && value > 0) {
      fixingPreviewDurationRef.current = false;
      return;
    }

    if (value === Infinity && !fixingPreviewDurationRef.current) {
      fixingPreviewDurationRef.current = true;

      try {
        video.currentTime = 10000000;
      } catch (error) {
        fixingPreviewDurationRef.current = false;
      }
    }
  };

  const handlePreviewDurationChange = () => {
    const video = previewVideoRef.current;

    if (!video) return;

    const value = video.duration;

    if (!Number.isFinite(value) || value <= 0) return;

    if (fixingPreviewDurationRef.current) {
      fixingPreviewDurationRef.current = false;

      try {
        video.currentTime = 0;
      } catch (error) {}
    }
  };

  /* ================= LOADED METADATA ================= */

  const onLoadedMetadata = () => {
    normalizeMainDuration();

    const video = videoRef.current;

    if (!video) return;

    video.volume = volume;
    video.playbackRate = speed;
  };

  /* ================= TIME UPDATE ================= */

  const onTimeUpdate = () => {
    const video = videoRef.current;

    if (!video || fixingDurationRef.current) return;

    const value = video.currentTime;

    if (!Number.isFinite(value) || value < 0) return;

    setCurrentTime(value);

    if (
      Number.isFinite(video.duration) &&
      video.duration > 0 &&
      video.duration !== duration
    ) {
      setDuration(video.duration);
    }
  };

  /* ================= PLAYING ================= */

  const handlePlaying = () => {
    setIsPlaying(true);
  };

  /* ================= PAUSE ================= */

  const handlePause = () => {
    setIsPlaying(false);
  };

  /* ================= PLAY / PAUSE ================= */

  const togglePlayPause = async () => {
    const video = videoRef.current;

    if (!video) return;

    try {
      if (video.paused) {
        if (duration > 0 && currentTime >= duration - 0.05) {
          video.currentTime = 0;
          setCurrentTime(0);
        }

        await video.play();
      } else {
        video.pause();
      }
    } catch (error) {
      console.error("Video playback error:", error);
    }
  };

  /* ================= SEEK ================= */

  const handleSeek = (time) => {
    const video = videoRef.current;

    if (!video || !Number.isFinite(duration) || duration <= 0) return;

    let newTime = Number(time);

    if (!Number.isFinite(newTime)) return;

    newTime = Math.max(0, Math.min(newTime, duration));

    try {
      video.currentTime = newTime;
      setCurrentTime(newTime);
    } catch (error) {
      console.error("Video seek error:", error);
    }
  };

  /* ================= VOLUME ================= */

  const handleVolumeChange = (val) => {
    if (!videoRef.current) return;

    videoRef.current.volume = val;
    setVolume(val);
  };

  /* ================= SPEED ================= */

  const handleSpeedChange = (val) => {
    if (!videoRef.current) return;

    videoRef.current.playbackRate = val;
    setSpeed(val);
    setShowSpeedOptions(false);
  };

  /* ================= SEEK BACKWARD ================= */

  const seekBackward = () => {
    if (!videoRef.current || duration <= 0) return;

    handleSeek(Math.max(videoRef.current.currentTime - 5, 0));
  };

  /* ================= SEEK FORWARD ================= */

  const seekForward = () => {
    if (!videoRef.current || duration <= 0) return;

    handleSeek(Math.min(videoRef.current.currentTime + 5, duration));
  };

  /* ================= NEXT VIDEO ================= */

  const nextVideo = () => {
    if (postArray.length <= 1) return;

    setCurrentIndex((index) => (index + 1) % postArray.length);
  };

  /* ================= PREVIOUS VIDEO ================= */

  const prevVideo = () => {
    if (postArray.length <= 1) return;

    setCurrentIndex(
      (index) => (index - 1 + postArray.length) % postArray.length
    );
  };

  /* ================= FULLSCREEN ================= */

  const toggleFullScreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error("Fullscreen error:", error);
    }
  };

  /* ================= FULLSCREEN CHANGE ================= */

  useEffect(() => {
    const onFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", onFullScreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", onFullScreenChange);
    };
  }, []);

  /* ================= RESET VIDEO STATE ================= */

  useEffect(() => {
    fixingDurationRef.current = false;
    fixingPreviewDurationRef.current = false;

    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setHoverProgress(null);
    setShowPreview(false);
  }, [currentIndex, post]);

  /* ================= PAUSE ON RESIZE ================= */

  useEffect(() => {
    const handleResize = () => {
      if (!videoRef.current) return;

      videoRef.current.pause();
      setIsPlaying(false);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /* ================= HOVER PREVIEW ================= */

  const handleProgressMouseMove = (e) => {
    if (
      !videoRef.current ||
      !Number.isFinite(duration) ||
      duration <= 0
    ) {
      setShowPreview(false);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();

    if (!rect.width) return;

    const position = e.clientX - rect.left;
    const percentage = Math.min(Math.max(position / rect.width, 0), 1);
    const time = duration * percentage;

    if (!Number.isFinite(time)) return;

    setHoverProgress(time);
    setShowPreview(true);
  };

  const handleProgressMouseLeave = () => {
    setShowPreview(false);
  };

  /* ================= CANVAS PREVIEW ================= */

  useEffect(() => {
    if (
      !showPreview ||
      hoverProgress === null ||
      !Number.isFinite(hoverProgress) ||
      !previewVideoRef.current ||
      !previewCanvasRef.current
    ) return;

    const previewVideo = previewVideoRef.current;
    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    try {
      previewVideo.currentTime = hoverProgress;
    } catch (error) {
      return;
    }

    const drawFrame = () => {
      if (previewVideo.readyState >= 2) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(
          previewVideo,
          0,
          0,
          canvas.width,
          canvas.height
        );
      }
    };

    previewVideo.addEventListener("seeked", drawFrame, { once: true });

    const timeout = setTimeout(drawFrame, 150);

    return () => {
      previewVideo.removeEventListener("seeked", drawFrame);
      clearTimeout(timeout);
    };
  }, [hoverProgress, showPreview, currentIndex, post]);

  /* ================= VIDEO ENDED ================= */

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);

    if (videoRef.current) {
      try {
        videoRef.current.currentTime = 0;
      } catch (error) {}
    }
  };

  /* ================= OPEN VIDEO ================= */

  const openVideo = (index) => {
    setCurrentIndex(index);
    setShowViewer(true);
  };

  /* ================= CLOSE VIEWER ================= */

  const closeViewer = () => {
    videoRef.current?.pause();

    setIsPlaying(false);
    setShowViewer(false);
  };

  /* ================= PLAYER ================= */

  const renderPlayer = (viewer = false) => (
    <div
      ref={containerRef}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => {
        setShowControls(false);
        setShowVolume(false);
        setShowSpeedOptions(false);
        setShowPreview(false);
      }}
      className={`group relative h-full w-full select-none overflow-hidden bg-gray-950 ${
        isFullScreen
          ? "rounded-none"
          : viewer
          ? "rounded-[24px]"
          : "border border-gray-200 shadow-sm"
      }`}
    >
      {/* ================= MAIN VIDEO ================= */}

      <video
        key={currentVideoSource}
        ref={videoRef}
        src={currentVideoSource}
        className={`h-full w-full bg-gray-950 object-cover ${
          isFullScreen ? "max-h-screen" : ""
        }`}
        autoPlay
        onLoadedMetadata={onLoadedMetadata}
        onDurationChange={handleDurationChange}
        onTimeUpdate={onTimeUpdate}
        onPlaying={handlePlaying}
        onPause={handlePause}
        onEnded={handleEnded}
        controls={false}
        preload="auto"
        playsInline
      />

      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/30" />

      {/* ================= VIDEO BADGE ================= */}

      <div className="absolute left-4 top-4 z-10 flex items-center gap-2 rounded-full bg-black/45 px-3 py-1.5 text-xs font-semibold text-white shadow-sm backdrop-blur-md">
        <MdVideocam size={15} />
        Video
      </div>

      {/* ================= VIDEO NUMBER ================= */}

      {postArray.length > 1 && (
        <div className="absolute right-4 top-4 z-20 flex items-center gap-1.5 rounded-full bg-black/65 px-3 py-1.5 text-xs font-semibold text-white shadow-sm backdrop-blur-md">
          <MdVideocam size={15} />
          <span>
            {currentIndex + 1} / {postArray.length}
          </span>
        </div>
      )}

      {/* ================= HIDDEN PREVIEW ================= */}

      <video
        key={`preview-${currentVideoSource}`}
        ref={previewVideoRef}
        src={currentVideoSource}
        style={{ display: "none" }}
        muted
        playsInline
        preload="auto"
        onLoadedMetadata={normalizePreviewDuration}
        onDurationChange={handlePreviewDurationChange}
      />

      {/* ================= PREVIOUS / NEXT ================= */}

      {postArray.length > 1 && (
        <>
          <button
            type="button"
            onClick={prevVideo}
            aria-label="Previous video"
            className="absolute left-3 top-1/2 z-30 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white shadow-md backdrop-blur-md transition duration-200 hover:scale-105 hover:bg-black/60"
          >
            <GrFormPrevious size={24} />
          </button>

          <button
            type="button"
            onClick={nextVideo}
            aria-label="Next video"
            className="absolute right-3 top-1/2 z-30 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white shadow-md backdrop-blur-md transition duration-200 hover:scale-105 hover:bg-black/60"
          >
            <MdOutlineNavigateNext size={24} />
          </button>
        </>
      )}

      {/* ================= BOTTOM PLAYER ================= */}

      <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/90 via-black/45 to-transparent pb-4 pt-16 transition-opacity duration-200">
        <div
          className="relative px-4"
          onMouseMove={handleProgressMouseMove}
          onMouseLeave={handleProgressMouseLeave}
        >
          <SeekBar
            currentTime={Number.isFinite(currentTime) ? currentTime : 0}
            duration={Number.isFinite(duration) ? duration : 0}
            onSeek={handleSeek}
          />

          {showPreview &&
            Number.isFinite(hoverProgress) &&
            duration > 0 && (
              <div
                className="pointer-events-none absolute -top-[130px] overflow-hidden rounded-xl border border-white/20 bg-gray-950 p-1.5 shadow-2xl"
                style={{
                  width: "176px",
                  left: (() => {
                    if (
                      !containerRef.current ||
                      !Number.isFinite(hoverProgress) ||
                      duration <= 0
                    ) return 0;

                    const rect =
                      containerRef.current.getBoundingClientRect();
                    const previewWidth = 176;

                    let position =
                      (hoverProgress / duration) * rect.width -
                      previewWidth / 2;

                    if (position < 4) position = 4;

                    if (
                      position + previewWidth >
                      rect.width - 4
                    ) {
                      position =
                        rect.width - previewWidth - 4;
                    }

                    return position;
                  })(),
                }}
              >
                <canvas
                  ref={previewCanvasRef}
                  width={320}
                  height={180}
                  className="aspect-video w-full rounded-lg object-cover"
                />

                <div className="flex items-center justify-center py-1">
                  <span className="text-[11px] font-semibold text-white">
                    {formatTime(hoverProgress)}
                  </span>
                </div>
              </div>
            )}
        </div>

        <Controls
          isPlaying={isPlaying}
          onPlayPause={togglePlayPause}
          onSeekBackward={seekBackward}
          onSeekForward={seekForward}
          volume={volume}
          onVolumeChange={handleVolumeChange}
          speed={speed}
          onSpeedChange={handleSpeedChange}
          isFullScreen={isFullScreen}
          onToggleFullScreen={toggleFullScreen}
          showVolume={showVolume}
          setShowVolume={setShowVolume}
          showSpeedOptions={showSpeedOptions}
          setShowSpeedOptions={setShowSpeedOptions}
          largeScreen={largeScreen}
        />
      </div>
    </div>
  );

  /* ================= FEED PREVIEW ================= */

  const renderFeedPreview = () => (
    <button
      type="button"
      onClick={() => openVideo(0)}
      className="relative isolate block min-h-64 h-full w-full overflow-hidden bg-black xs:min-h-[400px] lg:min-h-[450px]"
    >
      <video
        key={getVideoThumbnailSource(postArray[0])}
        src={getVideoThumbnailSource(postArray[0])}
        onLoadedData={handleFeedPreviewLoadedMetadata}
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
        muted
        playsInline
        preload="auto"
      />

      <div className="pointer-events-none absolute inset-0 z-[1] bg-black/5" />

      {/* ================= CENTER PLAY BUTTON ================= */}

      <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-purple-400/90 shadow-xl backdrop-blur-[2px]">
          <FaPlay
            size={21}
            className="translate-x-[1px] text-white"
          />
        </div>
      </div>

      {/* ================= MEDIA COUNT ================= */}

      {postArray.length > 1 && (
        <div className="pointer-events-none absolute right-3 top-3 z-20 flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1.5 text-sm font-semibold text-white shadow-lg backdrop-blur-sm">
          <MdVideocam size={18} />
          <span>{postArray.length}</span>
        </div>
      )}
    </button>
  );

  /* ================= VIDEO VIEWER ================= */

  const renderViewer = () => {
    if (!showViewer) return null;

    return (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4"
        onClick={closeViewer}
      >
        <button
          type="button"
          onClick={closeViewer}
          aria-label="Close"
          className="absolute right-5 top-5 z-[110] flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-2xl text-white transition hover:bg-white/25"
        >
          ×
        </button>

        <div
          className="h-[80dvh] w-full max-w-5xl overflow-hidden rounded-[24px]"
          onClick={(e) => e.stopPropagation()}
        >
          {renderPlayer(true)}
        </div>
      </div>
    );
  };

  if (postArray.length === 0) return null;

  return (
    <>
      {renderFeedPreview()}
      {renderViewer()}
    </>
  );
};

export default VideoSlider;